import { useEffect, useMemo, useState } from "react";

import { useGetCompanyRepresentationOptionsMutation } from "../features/company-representation/companyRepresentationApiSlice";
import {
	buildCompanyRepresentation,
	toCompanyRepresentationSelectOptions,
} from "../utils/company-representation";

/**
 * LC-481: loads the "Company Being Represented" options once a Loan
 * Originator is selected, and keeps the broker's choice.
 *
 * - No DBAs on the company/branch (or manual originator): the field is
 *   hidden and `isRequired` is false.
 * - One or more DBAs: the field is required and nothing is preselected,
 *   even with a single DBA. Changing the Loan Originator's org resets it.
 */
const useCompanyRepresentation = ({ organization, disabled = false }) => {
	const [getOptions, { isLoading }] = useGetCompanyRepresentationOptionsMutation();

	const [result, setResult] = useState(null);
	const [selectedValue, setSelectedValue] = useState("");

	const orgId = disabled ? null : organization?.org_id;

	useEffect(() => {
		let cancelled = false;
		setSelectedValue("");
		setResult(null);

		if (orgId === null || orgId === undefined || orgId === "") {
			return undefined;
		}

		getOptions({ orgId })
			.unwrap()
			.then((response) => {
				if (!cancelled) {
					setResult(response);
				}
			})
			.catch((err) => {
				// Soft fail: loan creation continues with the legal name (LC-669).
				console.error("[LC-481] Company Being Represented lookup failed:", err);
				if (!cancelled) {
					setResult(null);
				}
			});

		return () => {
			cancelled = true;
		};
	}, [orgId, getOptions]);

	const options = useMemo(
		() =>
			result?.has_dba ? toCompanyRepresentationSelectOptions(result?.options) : [],
		[result]
	);

	const selectedOption = useMemo(
		() => options.find((option) => option.value === selectedValue) || null,
		[options, selectedValue]
	);

	const isRequired = options.length > 0;

	const companyRepresentation = useMemo(
		() =>
			isRequired ? buildCompanyRepresentation(selectedOption, result?.is_branch) : null,
		[isRequired, selectedOption, result]
	);

	return {
		options,
		selectedValue,
		setSelectedValue,
		isLoading,
		isRequired,
		isMissing: isRequired && !companyRepresentation,
		companyRepresentation,
	};
};

export default useCompanyRepresentation;
